// Sign in / out. Privy in staging/production; dev email login locally (DEV_AUTH on both sides).
import { api } from "./api";
import { clientEnv } from "./env";
import { saveDevToken, session } from "./session";
import { identify, track } from "./telemetry";
import type { Me } from "@degen/shared/api";

let onSignedIn: (() => void) | null = null;

/** Called once by the cloud-sync module: what to do after a sign-in (link + sync the save). */
export function whenSignedIn(fn: () => void) {
  onSignedIn = fn;
}

async function finish(provider: "privy" | "dev", email: string | null) {
  const me = await api<Me>("GET", "/me");
  session.value = { provider, email: me.email ?? email, userId: me.id, handle: me.handle, isAdmin: me.isAdmin, cosmetics: me.cosmetics };
  identify(me.id);
  track("signed_in", { provider });
  onSignedIn?.();
}

/** Privy step 1: email a one-time code. */
export async function requestEmailCode(email: string) {
  const { sendEmailCode } = await import("./privyAuth");
  await sendEmailCode(email);
}

/** Privy step 2: verify the code, then create/link the account on our server. */
export async function verifyEmailCode(email: string, code: string) {
  const { loginWithEmailCode } = await import("./privyAuth");
  session.value = { provider: "privy", email }; // so api() asks Privy for the token
  try {
    await finish("privy", await loginWithEmailCode(email, code));
  } catch (e) {
    session.value = null;
    throw e;
  }
}

export async function signInDev(email: string) {
  const r = await api<{ token: string }>("POST", "/auth/dev", { email }, { auth: false });
  saveDevToken(r.token);
  await finish("dev", email);
}

export async function signOut() {
  if (session.value?.provider === "privy") await (await import("./privyAuth")).privyLogout();
  saveDevToken(null);
  session.value = null;
}

/** On boot: restore a dev session silently; Privy restores itself when its module loads. */
export async function restoreSession() {
  try {
    if (clientEnv.devAuth && localStorage.getItem("degen-world:devtoken")) await finish("dev", null);
    else if (clientEnv.privyAppId && localStorage.getItem("degen-world:was-signed-in") === "1") {
      const { hasPrivySession } = await import("./privyAuth");
      session.value = { provider: "privy", email: null };
      if (await hasPrivySession()) await finish("privy", null);
      else session.value = null;
    }
  } catch {
    saveDevToken(null);
  }
}

session.subscribe((s) => {
  try {
    if (s) localStorage.setItem("degen-world:was-signed-in", "1");
    else localStorage.removeItem("degen-world:was-signed-in");
  } catch {
    /* ignore */
  }
});
